/**
 * WEBE-26603 — server-side Experiment module.
 *
 * Renders the assigned variant's block(s), chosen from `pageContext.bucket` — the visitor's `ab_bucket` cookie,
 * forwarded by Fastly as `X-AB-Bucket` (read in the page's serverSideProps). No bucket (no consent yet, or a
 * crawler) renders the original and isn't counted. The variant is chosen on the server with no client swap →
 * zero flicker. `<AbExposure>` records the exposure to Amplitude once on the client after hydration. The
 * bucket itself is assigned at consent, site-wide (useBucketCookie in `_app`).
 */
import { isBucket } from '@/utils/ab/bucket';
import { type TypeName, Modules } from './index';
import { AbExposure } from '@/components/ab/AbExposure';
import { isExperimentActive, experimentSlot } from '@/utils/ab/experiments';

type ExperimentProps = {
  pageContext?: { bucket?: string | null } & Record<string, unknown>;
  treatmentVariantKey?: string | null;
  active?: boolean | null;
  experimentKey: string;
  treatment: TypeName[];
  control: TypeName[];
};

const Experiment = ({
  treatmentVariantKey,
  experimentKey,
  pageContext,
  treatment,
  control,
  active,
}: ExperimentProps) => {
  const bucket = isBucket(pageContext?.bucket) ? pageContext.bucket : null;
  // Paused (`active === false`): render Control for everyone and emit no exposure. Same rule serverSideProps
  // uses for `Vary`.
  const isActive = isExperimentActive(active);
  // `bucket` decides which slot renders (no bucket → control) — the same rule serverSideProps uses to prune the
  // other slot. `treatmentVariantKey` is only the Amplitude variant label reported on exposure (default
  // 'treatment'); `||` so a cleared field ('') still reports 'treatment'.
  const showTreatment = experimentSlot(active, bucket) === 'treatment';
  const variant = showTreatment ? treatmentVariantKey || 'treatment' : 'control';
  // Only visitors with a bucket are in the test: someone seeing the original because they have no bucket yet
  // would otherwise be counted as control without having been randomly assigned.
  const inTest = isActive && bucket !== null;

  return (
    <>
      {inTest ? <AbExposure experimentKey={experimentKey} variant={variant} /> : null}
      <Modules modules={showTreatment ? treatment : control} context={pageContext} />
    </>
  );
};

export default Experiment;
